import { __rest } from "tslib";
import React, { createContext, useCallback, useEffect, useMemo, useState, } from 'react';
import { useIsMounted } from '../useIsMounted';
/**
 * Basically a boolean trigger and some fancy props. We use this to help manage our dynamically loaded components
 * that need to load based on user input.
 *
 * E.g. the loadable version DaEditor that loads when the editor
 * is clicked on.
 *
 * Usage:
 * ```jsx
 * const {isLoadingTriggered, triggerProps} = useLoadableComponent()
 *
 * return (
 *    <>
 *      <button {...triggerProps}>Load that component!</button>
 *      {isLoadingTriggered && <LoadableDaEditorWrapper />}
 *    </>
 * )
 * ```
 */
export function useLoadableComponent(_a) {
    var _b = _a === void 0 ? {} : _a, forceLoadOnMount = _b.forceLoadOnMount;
    var _c = useState(false), isLoaded = _c[0], setIsLoaded = _c[1];
    var _d = useState(false), isLoadingTriggered = _d[0], setIsLoadingTriggered = _d[1];
    var isMounted = useIsMounted();
    var triggerLoad = useCallback(function () {
        !isLoadingTriggered && setIsLoadingTriggered(true);
    }, [isLoadingTriggered]);
    var triggerProps = useMemo(function () { return ({
        onDragEnter: triggerLoad,
        onClick: triggerLoad,
    }); }, [triggerLoad]);
    var finishLoading = useCallback(function () {
        setIsLoaded(true);
    }, []);
    useEffect(function () {
        // We do the window check here because Suspense is client-side only.
        // SSR will throw an exception if we don't.
        if (isMounted && forceLoadOnMount && typeof window !== 'undefined') {
            triggerLoad();
        }
    }, [forceLoadOnMount, isMounted, triggerLoad]);
    return {
        triggerProps: triggerProps,
        isLoadingTriggered: isLoadingTriggered,
        triggerLoad: triggerLoad,
        isLoaded: isLoaded,
        finishLoading: finishLoading,
    };
}
/**
 *
 * A context version of our useLoadableComponent hook. Refer there for more info.
 *
 * Please use `<LoadableComponentContextProvider />` instead of `<LoadableComponentContext.Provider />`.
 *
 * Usage:
 * ```jsx
 * const { isLoadingTriggered, triggerProps } = useContext(LoadableComponentContext)
 * ```
 */
export var LoadableComponentContext = createContext({
    isLoadingTriggered: false,
    isLoaded: false,
    triggerProps: {
        onClick: function () { return null; },
        onDragEnter: function () { return null; },
    },
    finishLoading: function () { return null; },
    triggerLoad: function () { return null; },
});
/**
 * Bundles our `useLoadableComponent` hook with our `LoadableComponentContext`.
 */
export var LoadableComponentContextProvider = function (_a) {
    var children = _a.children, options = __rest(_a, ["children"]);
    var loadableComponentState = useLoadableComponent(options);
    var memoizedLoadableComponentState = useMemo(function () { return loadableComponentState; }, [loadableComponentState]);
    return (React.createElement(LoadableComponentContext.Provider, { value: memoizedLoadableComponentState }, children));
};
//# sourceMappingURL=useLoadableComponent.js.map